import React, { useState, useEffect, useRef } from 'react';

interface CounterProps {
  end: number;
  duration?: number;
  suffix?: string;
}

const Counter: React.FC<CounterProps> = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (countRef.current) {
      observer.observe(countRef.current);
    }

    return () => {
      if (countRef.current) {
        observer.unobserve(countRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTime: number;
    let animationFrame: number;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      setCount(Math.floor(progress * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, [isVisible, end, duration]);

  return (
    <div ref={countRef} className="text-6xl font-extrabold text-primary">
      {count}{suffix}
    </div>
  );
};

const FactCounter: React.FC = () => {
  const stats = [
    {
      number: 35,
      suffix: 'K+',
      title: 'Youth Skilled',
      description: 'through structured training programs',
      icon: '👥',
      bgColor: 'bg-primary-50',
      iconColor: 'text-primary',
      accentColor: 'bg-primary'
    },
    {
      number: 2,
      suffix: 'K+',
      title: 'Individuals Connected',
      description: 'with employment opportunities',
      icon: '💼',
      bgColor: 'bg-secondary-50',
      iconColor: 'text-secondary-700',
      accentColor: 'bg-secondary'
    },
    {
      number: 20,
      suffix: 'K+',
      title: 'Savings Accounts Enabled',
      description: 'to promote financial inclusion',
      icon: '💰',
      bgColor: 'bg-primary-50',
      iconColor: 'text-primary',
      accentColor: 'bg-primary'
    },
    {
      number: 5,
      suffix: 'K+',
      title: 'Centres Covered',
      description: 'across multiple regions',
      icon: '🏢',
      bgColor: 'bg-secondary-50',
      iconColor: 'text-secondary-700',
      accentColor: 'bg-secondary'
    }
  ];

  return (
    <div className="relative bg-gradient-to-br from-white via-primary-50 to-secondary-50 py-20">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-200 rounded-full mix-blend-multiply filter blur-xl opacity-40 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-secondary-200 rounded-full mix-blend-multiply filter blur-xl opacity-40 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-primary-300 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-primary mb-4">
            Our Impact <span className="text-secondary">in Numbers</span>
          </h2>
          <p className="text-gray-700 text-lg max-w-2xl mx-auto">
            Transforming lives through education, skill development, and financial empowerment
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div 
              key={index}
              className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 p-8 border-2 border-gray-100 hover:border-primary hover:scale-105 animate-fadeIn overflow-hidden"
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              {/* Background accent */}
              <div className={`absolute top-0 right-0 w-32 h-32 ${stat.bgColor} rounded-full -mr-16 -mt-16 opacity-50 group-hover:opacity-70 transition-opacity duration-500`}></div>
              
              {/* Icon */}
              <div className={`relative text-5xl mb-4 transform group-hover:scale-110 transition-transform duration-300 ${stat.iconColor}`}>
                {stat.icon}
              </div>

              {/* Counter */}
              <div className="relative mb-2">
                <Counter end={stat.number} suffix={stat.suffix} />
              </div>

              {/* Title */}
              <h3 className="relative text-xl font-bold text-primary mb-2">
                {stat.title}
              </h3>

              {/* Description */}
              <p className="relative text-gray-600 text-sm leading-relaxed">
                {stat.description}
              </p>

              {/* Bottom Accent Line */}
              <div className={`absolute bottom-0 left-0 right-0 h-1 ${stat.accentColor} rounded-b-2xl transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500`}></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FactCounter;